import { useEffect } from 'react';
import { Loader } from '@servicetitan/web-components';
import appManager from '@app/appManager';
import environment from '@lib/environment';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { buildTextCountProducerMfeData } from '../lib/buildTextCountProducerMfeData';

const logger = appManager.logger('UnreadTextCount');

/**
 * Hidden `comm-hub-text-list` mount whose only job is to write the team-wide
 * unread conversation count into the shared notifications registry.
 *
 * It renders the real conversation-list DOM -- there is no data-only variant, and
 * the headless bundle is banned outright. comm-hub's Pusher channel refcount is a
 * module-level `WeakMap` and `loadScripts` dedupes by script `src`, so two mounts
 * of the *same* URL share one refcount (correct) while two mounts of *different*
 * URLs get two refcounts over one `window.pusherInstance`. In that case, leaving
 * the Texts page drops the rail's refcount to zero and physically unsubscribes
 * the channel this producer still believes it holds -- the count then freezes for
 * the whole session while looking healthy. Hence: plain `Loader` only, never
 * `HeadlessLoader`, and always `environment.COMM_HUB_TEXT_LIST_URL` -- the same
 * key `InboxTextsPage` passes to the Texts rail as `commHubTextListUrl`.
 *
 * Hiding is the clip container's job (`UnreadTextCountMount`), not this
 * component's.
 */
const UnreadTextCountProducer = () => {
  const { user } = useAuth();
  const src = environment.COMM_HUB_TEXT_LIST_URL;

  useEffect(() => {
    if (!src) {
      // Both Loader fallbacks are null, so an absent or unreachable bundle is
      // otherwise completely silent -- a developer would see a permanently empty
      // badge with nothing on the console. Say it once.
      logger.warn(
        'VITE_COMM_HUB_TEXT_LIST_URL is not set; the unread text count will never populate',
      );
    }
  }, [src]);

  if (!user || !src) {
    return null;
  }

  const data = buildTextCountProducerMfeData(
    `${environment.API_URL}/bffproxy/${environment.COMM_HUB_BFF_PREFIX}`,
    user.org.id,
  );

  return (
    <Loader src={src} data={data} loadingFallback={null} errorFallback={null} />
  );
};

export default UnreadTextCountProducer;
